export default function Projects() {
  const projects = [
    {
      title: 'CallPilot',
      description: 'AI-powered call assistant built at Hack Nation, helping sales teams navigate calls with real-time guidance, objection handling suggestions, and automated follow-up generation.',
      technologies: ['Python', 'React', 'OpenAI', 'AWS', 'TypeScript'],
      features: [
        'Real-time call transcription and analysis',
        'AI-generated objection handling suggestions',
        'Automated follow-up email drafting',
      ],
      image: './images/projects/callpilot.png',
      imageIcon: null,
      codeUrl: 'https://github.com/albertomenendezrda/callpilot-hack-nation',
      demoUrl: 'https://callpilot-frontend-pxib5typta-uc.a.run.app/',
      paperUrl: null,
      projectUrl: null,
    },
    {
      title: 'SpotOtc',
      description: 'Over-the-counter trading platform that streamlines deal flow and counterparty matching for financial institutions, reducing friction in private market transactions.',
      technologies: ['React', 'TypeScript', 'Node.js', 'AWS'],
      features: [
        'Counterparty discovery and matching engine',
        'Secure deal negotiation workflow',
        'Real-time trade status tracking',
      ],
      image: './images/projects/spototc.png',
      imageIcon: null,
      codeUrl: 'https://github.com/albertomenendezrda/spototc',
      demoUrl: null,
      paperUrl: null,
      projectUrl: null,
    },
    {
      title: 'Face Recognition System',
      description: 'AI-powered face recognition solution developed for industrial applications, combining deep learning with real-time computer vision to enable accurate identity verification at scale. Published as a Master\'s thesis at Comillas University.',
      technologies: ['Python', 'TensorFlow', 'OpenCV', 'Deep Learning'],
      features: [
        'Real-time face detection and recognition',
        'Industrial-grade accuracy and performance',
        'Privacy-focused design with on-premise processing',
      ],
      image: './images/projects/face-recognition.png',
      imageIcon: null,
      codeUrl: null,
      demoUrl: null,
      paperUrl: 'https://repositorio.comillas.edu/jspui/bitstream/11531/51549/1/TFM%20MIC%20-%20Menendez%20Ruiz%20de%20Azua%2C%20Alberto.pdf',
      projectUrl: 'https://repositorio.comillas.edu/xmlui/handle/11531/31473',
    },
    {
      title: 'CoffeeChatly',
      description: 'Coffee chat organizing and tracking app built for the MIT Sloan MBA Intro to Product Management course, helping students schedule, prepare for, and follow up on networking conversations with classmates, alumni, and recruiters.',
      technologies: ['React', 'TypeScript', 'Vite', 'Tailwind CSS'],
      features: [
        'Coffee chat scheduling, prep, and follow-up tracking',
        'Recruiting dashboard with networking metrics and deadlines',
        'Target company selection and job wishlist pipeline',
      ],
      image: './images/projects/coffeechatly.png',
      imageIcon: null,
      codeUrl: 'https://github.com/albertomenendezrda/coffeechatly',
      demoUrl: 'https://coffeechatly-pm-project.vercel.app',
      paperUrl: null,
      projectUrl: null,
    },
  ];

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Projects
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
            A selection of projects showcasing my work in AI, fintech, and product development
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-blue-500 transition group flex flex-col"
            >
              {/* Project Image */}
              <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-200 overflow-hidden flex-shrink-0">
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center group-hover:scale-105 transition duration-300">
                    <span className="text-6xl">{project.imageIcon}</span>
                  </div>
                )}
              </div>

              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  {project.title}
                </h3>

                <p className="text-sm md:text-base text-gray-700 mb-4">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Features */}
                <ul className="space-y-1 mb-6 flex-1">
                  {project.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Links */}
                {(project.codeUrl || project.demoUrl || project.paperUrl || project.projectUrl) && (
                  <div className="flex flex-wrap items-center gap-3">
                    {project.demoUrl && (
                      <a
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Live Demo
                      </a>
                    )}
                    {project.codeUrl && (
                      <a
                        href={project.codeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                        </svg>
                        View Code
                      </a>
                    )}
                    {project.paperUrl && (
                      <a
                        href={project.paperUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Read Paper
                      </a>
                    )}
                    {project.projectUrl && (
                      <a
                        href={project.projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Full Project
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
